/**
 * Stockage des données de paiement en attente d'authentification 3D Secure (SCA).
 *
 * Pourquoi localStorage et pas sessionStorage :
 * la redirection 3DS (`window.location.href = authUrl`) peut faire revenir
 * l'utilisateur dans un *autre onglet/contexte de navigation* (surtout sur
 * mobile). `sessionStorage` est lié à l'onglet et serait alors vide au retour
 * sur `#/sca-callback` → erreur « Aucune donnée de paiement trouvée en session ».
 * `localStorage` est partagé entre tous les onglets de la même origine, ce qui
 * rattrape le cas « retour dans un nouvel onglet du même navigateur ».
 *
 * Depuis le passage à localStorage, ~13 % des retours 3DS arrivent quand même
 * avec `storageAvailable=true` mais storage vide (issue « SCA: payment data
 * missing at 3DS return ») : hypothèse d'un retour dans un AUTRE contexte
 * navigateur (webview in-app → navigateur système, partition différente).
 * Pour le prouver, ce module ajoute :
 *  - une écriture DOUBLE localStorage + sessionStorage (ceinture/bretelles,
 *    et `storage_source` au retour dit lequel a réellement servi) ;
 *  - un `device_id` persistant écrit une fois par navigateur : s'il est absent
 *    au retour, on est PAR CONSTRUCTION dans un autre contexte navigateur ;
 *  - un `attempt_id` généré avant la redirection et propagé dans la
 *    ScaReturnUrl (`#/sca-callback?attempt=<id>`) : il corrèle l'event
 *    « redirect initiated » et l'event de retour même quand le storage est
 *    vide ;
 *  - un diagnostic d'environnement au retour (referrer, navigation type,
 *    détection webview in-app…) via `getScaEnvironment()`.
 *
 * Contrepartie : `localStorage` persiste indéfiniment. On y ajoute donc un
 * timestamp + TTL pour ne jamais réutiliser un reste périmé (même logique que
 * le flow Alma, cf. useAlmaPaymentProcessor.ts).
 *
 * Observabilité (Sentry) : tags communs `flow=sca`, `sca_stage`
 * (persist|return), `payment_kind` (service|contract). Les events d'info
 * (`SCA: ...`) servent à mesurer le taux de succès ; les warnings/errors
 * isolent les modes d'échec (storage indisponible, write non relisible,
 * payload périmé).
 */

import * as Sentry from '@sentry/react';

const TS_KEY = 'scaPaymentTs';
const KIND_KEY = 'scaPaymentKind';
const DEVICE_ID_KEY = 'mondayDeviceId';
const TTL_MS = 30 * 60 * 1000; // 30 minutes

export type ScaPaymentKind = 'service' | 'contract';
export type ScaStorageSource = 'local' | 'session';
type ScaDataKey = 'scaPaymentData' | 'scaContractPaymentData';

export interface ScaPersistResult {
  /** Écrit ET relu à l'identique dans localStorage. */
  local: boolean;
  /** Écrit ET relu à l'identique dans sessionStorage. */
  session: boolean;
}

function randomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fallthrough */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Les deux stores, chacun protégé (l'accès même à `window.localStorage` peut throw). */
function stores(): Array<[ScaStorageSource, Storage]> {
  const out: Array<[ScaStorageSource, Storage]> = [];
  try {
    if (window.localStorage) out.push(['local', window.localStorage]);
  } catch {
    /* noop */
  }
  try {
    if (window.sessionStorage) out.push(['session', window.sessionStorage]);
  } catch {
    /* noop */
  }
  return out;
}

/** Lit une clé en essayant localStorage puis sessionStorage. */
function readKey(key: string): { value: string | null; source: ScaStorageSource | null } {
  for (const [source, store] of stores()) {
    try {
      const value = store.getItem(key);
      if (value !== null) return { value, source };
    } catch {
      /* essaie le store suivant */
    }
  }
  return { value: null, source: null };
}

/**
 * Sonde non destructive : localStorage est-il réellement utilisable dans ce
 * contexte (mode privé verrouillé, quota, partition webview…) ? Écrit puis
 * supprime une clé jetable.
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const probe = '__sca_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * Identifiant de contexte navigateur, persistant en localStorage.
 * Posé côté persist (avant la redirection 3DS) : s'il est ABSENT au retour
 * (`peekDeviceId() === null`), le retour a eu lieu dans un autre contexte
 * navigateur (autre app, autre partition) — c'est LA preuve recherchée pour
 * les retours « données absentes ». Ne throw jamais.
 */
export function getOrCreateDeviceId(): string | null {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id = randomId();
    localStorage.setItem(DEVICE_ID_KEY, id);
    // Round-trip : un storage factice (Safari ITP/privé) accepte setItem sans persister.
    return localStorage.getItem(DEVICE_ID_KEY) === id ? id : null;
  } catch {
    return null;
  }
}

/** Lecture SEULE du device id (ne crée rien : l'absence est un signal). */
export function peekDeviceId(): string | null {
  try {
    return localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    return null;
  }
}

/**
 * Identifiant de tentative 3DS, généré côté checkout AVANT la redirection et
 * propagé dans la ScaReturnUrl (`#/sca-callback?attempt=<id>`). Permet de
 * corréler dans Sentry l'event « redirect initiated » et l'event de retour
 * (completed OU missing) même quand le storage est vide au retour.
 */
let currentAttemptId: string | null = null;

export function newScaAttemptId(): string {
  currentAttemptId = randomId().slice(0, 8);
  return currentAttemptId;
}

export function getCurrentScaAttemptId(): string | null {
  return currentAttemptId;
}

/**
 * Stocke le payload SCA + le kind + l'horodatage dans localStorage ET
 * sessionStorage (double écriture : si le retour 3DS revient dans le même
 * onglet, sessionStorage suffit ; s'il revient dans un autre onglet du même
 * navigateur, localStorage rattrape). Ne throw jamais.
 *
 * Chaque écriture est vérifiée par round-trip (écrite PUIS relue à
 * l'identique) : certains navigateurs (Safari ITP, mode privé) acceptent
 * `setItem` sans persister. L'appelant peut donc tracer un échec de
 * persistance *avant* la redirection 3DS, ce qui distingue ce cas d'un retour
 * « données absentes » côté callback.
 */
export function setScaPayment(key: ScaDataKey, data: unknown, kind: ScaPaymentKind): ScaPersistResult {
  const result: ScaPersistResult = { local: false, session: false };

  let serialized: string;
  try {
    serialized = JSON.stringify(data);
  } catch (e) {
    Sentry.captureMessage('SCA: payload not serializable', {
      level: 'warning',
      tags: { flow: 'sca', sca_stage: 'persist', payment_kind: kind },
      extra: { key, error: e instanceof Error ? e.message : String(e) },
    });
    return result;
  }

  const errors: string[] = [];
  for (const [source, store] of stores()) {
    try {
      store.setItem(key, serialized);
      store.setItem(KIND_KEY, kind);
      store.setItem(TS_KEY, Date.now().toString());
      // Round-trip : confirme que la donnée est réellement relisible.
      result[source] = store.getItem(key) === serialized;
      if (!result[source]) errors.push(`${source}: write not readable back`);
    } catch (e) {
      // Stockage indisponible (navigation privée verrouillée, quota) : on ne
      // bloque pas le paiement, mais on trace explicitement la cause.
      errors.push(`${source}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  if (!result.local) {
    Sentry.captureMessage(
      result.session
        ? 'SCA: localStorage write failed (sessionStorage fallback OK)'
        : 'SCA: localStorage write failed',
      {
        level: 'warning',
        tags: { flow: 'sca', sca_stage: 'persist', payment_kind: kind },
        extra: { key, errors },
      }
    );
  }

  Sentry.addBreadcrumb({
    category: 'sca',
    level: 'info',
    message: 'SCA payment data persisted before 3DS redirect',
    data: { key, kind, ...result },
  });
  return result;
}

/** Renvoie le kind du paiement en attente ('service' par défaut). */
export function getScaPaymentKind(): ScaPaymentKind {
  return (readKey(KIND_KEY).value as ScaPaymentKind | null) ?? 'service';
}

/**
 * Renvoie le payload SCA brut (string JSON) + le store qui l'a fourni, si
 * présent ET non périmé. Purge le stockage si le TTL est dépassé (et le trace).
 */
export function getScaPayment(key: ScaDataKey): { raw: string | null; source: ScaStorageSource | null } {
  const ts = readKey(TS_KEY).value;
  if (ts && Date.now() - parseInt(ts, 10) > TTL_MS) {
    Sentry.captureMessage('SCA: stored payload expired (TTL)', {
      level: 'warning',
      tags: { flow: 'sca', sca_stage: 'return', payment_kind: getScaPaymentKind() },
      extra: { ageMs: Date.now() - parseInt(ts, 10), ttlMs: TTL_MS, key },
    });
    clearScaPayment();
    return { raw: null, source: null };
  }
  const { value, source } = readKey(key);
  return { raw: value, source };
}

/**
 * Diagnostic non destructif de l'état du stockage SCA, à attacher aux events
 * Sentry du callback pour comprendre un retour « données absentes » :
 * storage indisponible vs réellement vide (cross-onglet / webview) vs périmé.
 */
export function getScaDiagnostics(): {
  storageAvailable: boolean;
  hasServiceData: boolean;
  hasContractData: boolean;
  storedKind: ScaPaymentKind | null;
  ageMs: number | null;
  storageSource: ScaStorageSource | null;
} {
  const storageAvailable = isLocalStorageAvailable();
  const service = readKey('scaPaymentData');
  const contract = readKey('scaContractPaymentData');
  const ts = readKey(TS_KEY).value;
  return {
    storageAvailable,
    hasServiceData: service.value !== null,
    hasContractData: contract.value !== null,
    storedKind: readKey(KIND_KEY).value as ScaPaymentKind | null,
    ageMs: ts ? Date.now() - parseInt(ts, 10) : null,
    storageSource: service.source ?? contract.source,
  };
}

/**
 * Diagnostic d'environnement au retour 3DS : d'où vient-on, dans quel type de
 * navigateur est-on ? À poser en tags/context sur les events du callback pour
 * confirmer (ou infirmer) l'hypothèse « retour dans un autre contexte
 * navigateur ». Ne throw jamais.
 */
export function getScaEnvironment(): {
  referrer: string | null;
  navigationType: string | null;
  origin: string | null;
  historyLength: number | null;
  inAppBrowser: boolean;
  deviceId: string | null;
} {
  let navigationType: string | null = null;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    navigationType = nav?.type ?? null;
  } catch {
    /* noop */
  }
  let referrer: string | null = null;
  try {
    referrer = document.referrer || null;
  } catch {
    /* noop */
  }
  let historyLength: number | null = null;
  try {
    historyLength = window.history.length;
  } catch {
    /* noop */
  }
  let inAppBrowser = false;
  try {
    // Marqueurs usuels des webviews in-app (Meta, TikTok, Snapchat, Line,
    // Google App) + le `; wv)` des WebViews Android.
    inAppBrowser = /(FBAN|FBAV|FB_IAB|Instagram|TikTok|Snapchat|Line\/|GSA\/|; wv\))/i.test(
      navigator.userAgent || ''
    );
  } catch {
    /* noop */
  }
  return {
    referrer,
    navigationType,
    origin: typeof window !== 'undefined' ? window.location.origin : null,
    historyLength,
    inAppBrowser,
    deviceId: peekDeviceId(),
  };
}

/** Supprime toutes les clés SCA des deux stores (succès, erreur ou expiration). Ne throw jamais. */
export function clearScaPayment(): void {
  for (const [, store] of stores()) {
    try {
      store.removeItem('scaPaymentData');
      store.removeItem('scaContractPaymentData');
      store.removeItem(KIND_KEY);
      store.removeItem(TS_KEY);
    } catch {
      /* noop */
    }
  }
}
